import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check } from "lucide-react";

import { LOCALES, DEFAULT_LOCALE } from "@/i18n";

/**
 * LanguageSwitcher — top-right button with flag + native label.
 * Click opens a dropdown listing all 26 supported languages.
 */
export const LanguageSwitcher = ({ variant = "default" }) => {
  const { i18n } = useTranslation();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  // Read locale from i18n.language (single source of truth), with URL pathname
  // as fallback for SSR/pre-render. Strips region suffix ("en-US" → "en").
  const langFromI18n = (i18n.language || "").toLowerCase().split("-")[0];
  let currentCode = DEFAULT_LOCALE;
  if (langFromI18n && LOCALES.some((l) => l.code === langFromI18n)) {
    currentCode = langFromI18n;
  } else {
    try {
      const m = window.location.pathname.match(/^\/([a-z]{2})(\/|$)/);
      if (m && LOCALES.some((l) => l.code === m[1]) && m[1] !== DEFAULT_LOCALE) {
        currentCode = m[1];
      }
    } catch (e) {
      /* SSR */
    }
  }
  const current = LOCALES.find((l) => l.code === currentCode) || LOCALES[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (code) => {
    setOpen(false);
    if (code === current.code) return;
    try {
      localStorage.setItem("equisight_lng", code);
    } catch (e) {
      /* SSR / privacy mode */
    }
    // Compute target URL using the FULL pathname (including locale prefix),
    // then perform a full page reload so BrowserRouter basename is reset.
    const fullPath =
      (current.code === DEFAULT_LOCALE ? "" : `/${current.code}`) +
      window.location.pathname.replace(
        new RegExp(`^/${current.code}(?=/|$)`),
        ""
      );
    // Build a clean path: strip current locale prefix, then prepend new locale (if not default).
    const clean = fullPath.replace(
      new RegExp(`^/${current.code}(?=/|$)`),
      ""
    ) || "/";
    const targetPath =
      code === DEFAULT_LOCALE ? clean : `/${code}${clean === "/" ? "" : clean}`;
    window.location.href = targetPath + window.location.search + window.location.hash;
  };

  const compact = variant === "compact";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-trigger"
        aria-label={`Selecteer taal — huidige: ${current.label}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          "flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] transition-colors hover:border-[hsl(var(--forest))] " +
          (compact ? "px-2.5 py-1.5 text-sm" : "px-3 py-2 text-sm")
        }
      >
        <span
          aria-hidden="true"
          className={`fi fi-${current.country} rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.06)]`}
          style={{ width: 20, height: 14, display: "inline-block", backgroundSize: "cover" }}
        />
        {!compact && (
          <span className="hidden font-medium text-[hsl(var(--ink))] sm:inline">
            {current.code.toUpperCase()}
          </span>
        )}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 top-full z-50 mt-2 max-h-[min(80vh,520px)] w-64 overflow-y-auto rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] py-1.5 shadow-[0_30px_60px_-25px_rgba(26,37,30,0.4)]"
        >
          {LOCALES.map((l) => {
            const active = l.code === current.code;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => select(l.code)}
                data-testid={`language-option-${l.code}`}
                className={
                  "flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors " +
                  (active
                    ? "bg-[hsl(var(--surface-secondary))] text-[hsl(var(--ink))]"
                    : "text-[hsl(var(--ink))] hover:bg-[hsl(var(--surface-secondary))]")
                }
              >
                <span
                  aria-hidden="true"
                  className={`fi fi-${l.country} rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.06)]`}
                  style={{ width: 22, height: 16, display: "inline-block", backgroundSize: "cover", flexShrink: 0 }}
                />
                <span className="flex-1 truncate">{l.label}</span>
                <span className="text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))]">
                  {l.code}
                </span>
                {active && (
                  <Check className="h-3.5 w-3.5 text-[hsl(var(--forest))]" strokeWidth={2.5} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
